import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useMotionValue } from 'framer-motion';
import { Button, Tooltip } from '@heroui/react';
import { Tooltip as Tip } from '@/components/Tooltip';
import { toast } from '@/lib/toast';
import { useAuthStore } from '@/stores/authStore';
import { useShallow } from 'zustand/react/shallow';
import { useDataStore } from '@/stores/dataStore';
import { voiceLocation } from './voiceLocation';
import { leaveVoice, toggleSelfScreen } from '@/lib/voiceCommands';
import { isNativeApp } from '@/lib/isNativeApp';
import { useVoiceMediaSnapshot } from '@/lib/voiceMedia';
import { hasLiveVideoTrack } from '@/components/contentPane/voiceRoomView';
import { LiveBars } from '@/heroui/effects/LiveBars';
import { humanizeVoiceMediaError } from '@/components/contentPane/voiceErrors';
import { ScreenShareIcon, PhoneIcon } from '@/components/icons';

/**
 * Persistent "voice connected" bar for the HeroUI sidebar — sits directly
 * above the user widget and stays visible from any view while you're in a
 * voice channel (Discord-style). Shows the connected channel name plus a
 * screen-share toggle and a disconnect button. Built on HeroUI Button /
 * Tooltip. Renders nothing when not in voice.
 */
export function HeroVoiceBar() {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const myVoiceChannelId = useDataStore((s) => s.myVoiceChannelId);
  const selectGuild = useDataStore((s) => s.selectGuild);
  const selectChannel = useDataStore((s) => s.selectChannel);
  const selectDirectChannel = useDataStore((s) => s.selectDirectChannel);
  const currentChannelId = useDataStore((s) => s.currentChannelId);
  const reconnecting = useVoiceMediaSnapshot().reconnecting;
  // Where my voice session is, as plain fields compared shallowly: the bar
  // re-renders when that location changes, not on every guild or DM update
  // (a fresh object from a bare selector would loop — React #185).
  const location = useDataStore(useShallow((s) => voiceLocation(s, myVoiceChannelId)));
  const loc = location && { ...location, channelName: location.channelName ?? t('voiceBar.channelFallback') };
  const screenOn = useDataStore((s) =>
    me && myVoiceChannelId
      ? s.voiceByChannel[myVoiceChannelId]?.find((v) => v.userId === me.id)?.screenOn ?? false
      : false,
  );

  if (!myVoiceChannelId || !loc) return null;

  // Already looking at the voice channel? Then the jump is a no-op affordance.
  const viewingVoiceChannel = currentChannelId === myVoiceChannelId;

  function jumpToVoiceChannel() {
    if (!myVoiceChannelId) return;
    if (loc?.isDm) {
      selectDirectChannel(myVoiceChannelId);
    } else {
      if (loc?.guildId) selectGuild(loc.guildId);
      selectChannel(myVoiceChannelId);
    }
  }

  async function toggleScreen() {
    await toggleSelfScreen(
      { inVoice: true, screenOn },
      { onError: (msg) => toast.error(humanizeVoiceMediaError(msg)) },
    );
  }

  const inner = (
    <div className="studio-voice-row flex items-center gap-2">
      <Tip label={viewingVoiceChannel ? t('voiceBar.inThisChannel') : t('voiceBar.goToChannel')}>
        <button
          type="button"
          onClick={jumpToVoiceChannel}
          className="studio-voice-target flex min-w-0 flex-1 items-center gap-2.5 rounded-lg p-1 text-left transition-colors hover:bg-white/[0.04] disabled:cursor-default"
          disabled={viewingVoiceChannel}
        >
          {/* Live indicator: success-tinted tile with a gentle equalizer. */}
          <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-success/12 text-success">
            <LiveBars size={16} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-semibold text-foreground">
              {loc.channelName}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-muted">
              <span className={'size-1.5 shrink-0 rounded-full ' + (reconnecting ? 'bg-warning' : 'bg-success')} />
              <span className="truncate">
                {reconnecting
                  ? t('voiceBar.reconnecting')
                  : loc.guildName ? t('voiceBar.inVoiceGuild', { guild: loc.guildName }) : t('voiceBar.inVoice')}
              </span>
            </span>
          </span>
        </button>
      </Tip>
      <div className="studio-voice-actions flex shrink-0 items-center gap-0.5">
        {/* Screen share doesn't work in the mobile shell — hide it there. */}
        {!isNativeApp() && (
          <Tooltip delay={0}>
              <Button
                aria-label={screenOn ? t('voiceBar.stopShare') : t('voiceBar.share')}
                isIconOnly
                size="md"
                variant={screenOn ? 'primary' : 'ghost'}
                onPress={() => void toggleScreen()}
                className="group"
              >
                <ScreenShareIcon className="size-5" />
              </Button>
            <Tooltip.Content>{screenOn ? t('voiceBar.stopShare') : t('voiceBar.share')}</Tooltip.Content>
          </Tooltip>
        )}
        <Tooltip delay={0}>
            {/* Ghost (no red fill) to match the screen-share button — the red
                icon still signals "leave". Wobbles on hover. */}
            <Button aria-label={t('voiceBar.disconnect')} isIconOnly size="md" variant="ghost" className="group" onPress={() => leaveVoice()}>
              <PhoneIcon className="size-5 text-danger" />
            </Button>
          <Tooltip.Content>{t('voiceBar.disconnect')}</Tooltip.Content>
        </Tooltip>
      </div>
    </div>
  );

  return (
    <>
      {/* Floating draggable self-preview — only when sharing AND not currently
          looking at the voice channel (in-channel preview lives in the tile). */}
      <ScreenSharePip onStop={() => void toggleScreen()} hidden={viewingVoiceChannel} />
      {/* The caller (HeroSidebarFooter) owns the surrounding surface/chrome. */}
      <div className="px-2.5 py-2">{inner}</div>
    </>
  );
}

// Position persistence — the user can drag the PiP and we remember where.
const PIP_STORAGE_KEY = 'bunker.screenSharePip.pos.v1';
const PIP_FIXED_LEFT = 12;     // left-3
const PIP_FIXED_BOTTOM = 96;   // bottom-24
const PIP_WIDTH = 224;         // w-56
const PIP_HEIGHT_EST = 158;    // aspect-video (126) + footer strip (32)

function readPipPos(): { x: number; y: number } {
  try {
    const raw = localStorage.getItem(PIP_STORAGE_KEY);
    if (!raw) return { x: 0, y: 0 };
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as { x?: unknown }).x === 'number' &&
      typeof (parsed as { y?: unknown }).y === 'number'
    ) {
      return parsed as { x: number; y: number };
    }
  } catch {
    /* ignore */
  }
  return { x: 0, y: 0 };
}

/**
 * Floating self-preview of your own screen share — a small picture-in-picture
 * pinned above the voice bar. Draggable; the drop position is remembered
 * across sessions. Hidden when the user is currently looking at the voice
 * channel where they're sharing — there the preview lives inside the
 * participant tile instead, so the PiP would be redundant.
 */
function ScreenSharePip(props: { onStop: () => void; hidden: boolean }) {
  const { t } = useTranslation();
  const media = useVoiceMediaSnapshot();
  const stream = media.localScreenStream;
  const live = hasLiveVideoTrack(stream);
  const ref = useRef<HTMLVideoElement | null>(null);

  const initial = useMemo(() => readPipPos(), []);
  const x = useMotionValue(initial.x);
  const y = useMotionValue(initial.y);

  const [constraints, setConstraints] = useState({ left: 0, right: 0, top: 0, bottom: 0 });
  useEffect(() => {
    const recompute = () => {
      const initialTopFromViewportTop =
        window.innerHeight - PIP_FIXED_BOTTOM - PIP_HEIGHT_EST;
      const next = {
        left: -PIP_FIXED_LEFT,
        right: window.innerWidth - PIP_WIDTH - PIP_FIXED_LEFT,
        top: -initialTopFromViewportTop,
        bottom: PIP_FIXED_BOTTOM,
      };
      setConstraints(next);
      if (x.get() < next.left) x.set(next.left);
      if (x.get() > next.right) x.set(next.right);
      if (y.get() < next.top) y.set(next.top);
      if (y.get() > next.bottom) y.set(next.bottom);
    };
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [x, y]);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (v.srcObject !== stream) v.srcObject = stream;
    return () => {
      if (v.srcObject) v.srcObject = null;
    };
  }, [stream]);

  if (!live || props.hidden) return null;

  return (
    <motion.div
      drag
      dragMomentum={false}
      dragConstraints={constraints}
      dragElastic={0.05}
      style={{ x, y }}
      onDragEnd={() => {
        try {
          localStorage.setItem(
            PIP_STORAGE_KEY,
            JSON.stringify({ x: x.get(), y: y.get() }),
          );
        } catch {
          /* ignore quota errors */
        }
      }}
      className="fixed bottom-24 left-3 z-50 w-56 cursor-grab overflow-hidden rounded-xl border border-success/50 bg-black shadow-soft active:cursor-grabbing"
    >
      <div className="aspect-video w-full bg-black">
        <video ref={ref} autoPlay playsInline muted className="h-full w-full object-contain" />
      </div>
      <div className="flex items-center justify-between gap-2 bg-surface-secondary px-2 py-1">
        <span className="font-medium text-xs text-success">{t('voiceBar.yourScreen')}</span>
        <button
          type="button"
          onClick={props.onStop}
          onPointerDown={(e) => e.stopPropagation()}
          className="font-medium rounded-full bg-danger px-2 py-0.5 text-xs !text-white transition-opacity hover:opacity-90"
        >
          {t('voiceBar.stop')}
        </button>
      </div>
    </motion.div>
  );
}
